import { useState, useEffect } from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (scheduledAt: string | undefined) => void;
  /** "approve" (default) mostra opções; "reschedule" vai direto pro date picker */
  mode?: "approve" | "reschedule";
}

// Returns next Tuesday or Thursday at 09:00 local time as Date
const nextTueOrThu9am = (): Date => {
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  for (let i = 1; i <= 7; i++) {
    const candidate = new Date(d.getTime() + i * 86_400_000);
    const dow = candidate.getDay(); // 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, ...
    if (dow === 2 || dow === 4) return candidate;
  }
  return d;
};

const toInputValue = (d: Date): string => {
  // datetime-local expects "YYYY-MM-DDTHH:mm"
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const ScheduleModal = ({ isOpen, onClose, onSubmit, mode = "approve" }: Props) => {
  const [view, setView] = useState<"options" | "schedule">(
    mode === "reschedule" ? "schedule" : "options"
  );
  const [datetimeLocal, setDatetimeLocal] = useState<string>(toInputValue(nextTueOrThu9am()));

  // Reset view when re-opening
  useEffect(() => {
    if (isOpen) {
      setView(mode === "reschedule" ? "schedule" : "options");
      setDatetimeLocal(toInputValue(nextTueOrThu9am()));
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const handleScheduleSubmit = () => {
    const localDate = new Date(datetimeLocal);
    if (isNaN(localDate.getTime()) || localDate.getTime() < Date.now() + 60_000) {
      // disabled by validation below
      return;
    }
    onSubmit(localDate.toISOString());
  };

  const localDate = new Date(datetimeLocal);
  const isInvalidPast = !isNaN(localDate.getTime()) && localDate.getTime() < Date.now() + 60_000;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-modal-title"
    >
      <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
        {view === "options" ? (
          <>
            <h3 id="schedule-modal-title" className="text-lg font-semibold mb-4">Como aprovar?</h3>
            <div className="flex gap-3">
              <button
                type="button"
                className="flex-1 bg-emerald-600 text-white py-3 rounded font-medium"
                onClick={() => onSubmit(undefined)}
              >
                Postar agora
              </button>
              <button
                type="button"
                className="flex-1 bg-slate-200 py-3 rounded font-medium"
                onClick={() => setView("schedule")}
              >
                Agendar
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 id="schedule-modal-title" className="text-lg font-semibold mb-4">Agendar</h3>
            <label className="block text-xs text-slate-600 mb-1">Data e hora</label>
            <input
              type="datetime-local"
              value={datetimeLocal}
              onChange={(e) => setDatetimeLocal(e.target.value)}
              className="border rounded px-3 py-2 w-full mb-2"
              aria-label="data e hora"
            />
            {isInvalidPast && (
              <p className="text-xs text-red-600 mb-2">Data precisa ser pelo menos 1 minuto no futuro</p>
            )}
            <div className="flex gap-3 mt-3">
              <button
                type="button"
                className="flex-1 bg-slate-200 py-2 rounded font-medium"
                onClick={mode === "reschedule" ? onClose : () => setView("options")}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="flex-1 bg-emerald-600 text-white py-2 rounded font-medium disabled:opacity-50"
                onClick={handleScheduleSubmit}
                disabled={isInvalidPast}
              >
                Agendar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
